/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import axios from 'axios';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useMeStore } from '@/services/UserService/useMeStore';
export const useAccountsStore = defineStore('accountsStore', {
  state: () => {
    const {
      handleError,
      handleSuccess
    } = useSnackbarHandler();
    return {
      handleError,
      handleSuccess,
      accounts: {},
      accountsList: [],
      insights: false,
      alerts: {
        // Support both AngularJS-style error calls and Vue error handling
        error: message => {
          // If message is already an error object (from Vue), use handleError
          if (message && typeof message === 'object' && (message.response || message.message)) {
            handleError(message);
          } else {
            // If it's a simple string message (from AngularJS), handle it accordingly
            handleError(new Error(message || 'An error occurred'));
          }
        }
      }
    };
  },
  actions: {
    async listMyAccounts(refresh = false) {
      try {
        if (Object.keys(this.accounts).length && !refresh) {
          return this.accounts;
        }
        const response = await axios({
          url: '/me/accounts'
        });
        this.$patch({
          accounts: response.data.accounts
        });
        return response.data.accounts;
      } catch (error) {
        // Optional chaining is required: on a timeout or network error
        // `error.response` is undefined, and reading `error.response.data.message`
        // directly would throw a TypeError from inside this catch — aborting the
        // caller (e.g. HeaderComponent's init, which then skips loading the
        // HubSpot chat widget entirely). SC-46137. Prefer the server's curated
        // message, fall back to the generic axios message (e.g. on timeout).
        this.alerts.error(error.response?.data?.message || error.message);
        return [];
      }
    },
    async listAccounts(refresh = false, parameters = {}) {
      try {
        if (this.accountsList.length && !refresh) {
          return this.accountsList;
        }
        const response = await axios({
          url: '/accounts',
          method: 'GET',
          params: parameters
        });
        this.$patch({
          accountsList: response.data.accounts
        });
        return response.data.accounts;
      } catch (error) {
        this.alerts.error(error.response?.data?.message || error.message);
        return [];
      }
    },
    async listInvoiceClassTags() {
      try {
        const response = await axios({
          url: '/tags',
          method: 'GET'
        });
        return response.data.tags;
      } catch (error) {
        this.alerts.error(error.message || error.response?.data?.message);
        return [];
      }
    },
    async getSubscriptionCost(params) {
      try {
        const response = await axios({
          url: '/subscription/cost',
          method: 'GET',
          params
        });
        return response.data;
      } catch (error) {
        this.handleError(error);
        return [];
      }
    },
    async switchToDashboardView(isInsight) {
      const {
        getMe,
        me
      } = useMeStore();
      await getMe();
      try {
        this.$patch({
          insights: isInsight
        });
        const payload = {
          settings: {
            insightsView: isInsight
          }
        };
        await axios.put(`accounts/${me.id}`, payload);
      } catch (error) {
        this.alerts.error(error.response.data.message);
      }
    },
    async getuserAccountDetailsById(id) {
      try {
        const response = await axios({
          url: `accounts/${id}`,
          method: 'GET'
        });
        return response.data;
      } catch (error) {
        this.alerts.error(error.message || error.response?.data?.message);
        return [];
      }
    },
    async getAccountState(id) {
      try {
        const response = await axios({
          url: `/accounts/${id}/state`,
          method: 'GET'
        });
        return response.data;
      } catch (error) {
        this.handleError(error);
        return {};
      }
    },
    async createAccountAndSendInvitation(accountDetails) {
      try {
        const response = await axios.post('/accounts', accountDetails);
        this.listAccounts();
        this.handleSuccess('Account created successfully');
        return response.data;
      } catch (error) {
        this.handleError(error);
        return {};
      }
    },
    async updateAccount(accountId, accountDetails) {
      try {
        const response = await axios.put(`/accounts/${accountId}`, accountDetails);
        this.handleSuccess('Account updated successfully');
        return response.data;
      } catch (error) {
        this.handleError(error);
        return {};
      }
    },
    async inviteAccount(accountId, email) {
      try {
        const response = await axios.post(`/accounts/${accountId}/invite`);
        this.handleSuccess(`Sent email invitation to '${email}'`);
        return response.data;
      } catch (error) {
        this.handleError(error);
        return {};
      }
    },
    async archiveAccount(id, payload) {
      try {
        await axios({
          url: `/accounts/${id}/archive`,
          method: 'POST',
          params: payload
        });
        this.handleSuccess(payload.archive ? `Account archived successfully` : `Account enabled successfully`);
      } catch (error) {
        this.handleError(error);
      }
    },
    async deleteAccount(id, payload) {
      try {
        await axios.delete(`/accounts/${id}`, payload);
        this.handleSuccess('Account deleted successfully');
      } catch (error) {
        this.handleError(error);
      }
    }
  }
});